import React, {useEffect, useMemo} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, Tooltip, Typography, useTheme} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useFormContext, UseFormReturn, useWatch} from "react-hook-form";
import {useRecoilState, useRecoilValue} from "recoil";
import _ from "underscore";
import {IRADAncillary, IRaDetailView} from "../../models/models";
import {bookingDataValueState, counterSaleManagementValueState, raFillingAccordionValidationState} from "../../create-ra/state";
import {FormCheckbox, FormNumberInput, FormTextField} from "../../common/form-fields-wrappers";
import {formatCurrency, getLocaleByLang} from "../../utils";
import {deepClone} from "../../../../util/state/commonFunctions";
import {LANGUAGES, LOCALES} from "../../config";
import {AncillarySubtype} from "../../create-ra/ra-filling/config";
import {IIndexedAncillary, partitionChangeGroup} from "../../create-ra/ra-filling/ancillaries-info/changeGroup";
import ChangeGroupMultiSelect, {IChangeGroupOption} from "../../create-ra/ra-filling/ancillaries-info/ChangeGroupMultiSelect";

// interface AncillariesInfoTestProps {
//     updateAncillariesValues: (ancillariy: any) => void,
// }

const AncillariesInfo = () => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);
	const counterSaleManagement = useRecoilValue(counterSaleManagementValueState);
	const [raFillingAccordionValidation, setRaFillingAccordionValidation] = useRecoilState(raFillingAccordionValidationState);

	const raFillingDataForm = useFormContext<IRaDetailView>();

	const ancillariesFormValues = raFillingDataForm.watch('ancillariesInfo');
	useEffect(() => {
		console.log({ ancillariesFormValues })
	}, [ancillariesFormValues])

	//Set the editStartValue to the initial value of the ancillaries ( you can't add ancillary after booking creation for now)
	ancillariesFormValues?.booked.map((ancillary: any, index: number) => {
		ancillary.editStartValue = ancillary.ancillaryCount;
	});

	const ancillaryName = useMemo((): 'ancillaryNameIt' | 'ancillaryNameEn' => {
		let prop = 'ancillaryDefaultLabel';
		LANGUAGES.every((elem: string) => {
			if (i18n.language.includes(elem)) {
				prop = 'ancillaryName' + elem.charAt(0).toUpperCase() + elem.slice(1);
				return false;
			}
			return true;
		});

		return prop as 'ancillaryNameIt' | 'ancillaryNameEn';
	}, [i18n.language]);

	const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean) => {
		e.stopPropagation();
		
		setRaFillingAccordionValidation((prev) => {
			return {...prev, ["ancillariesInfo"]: expanded};
		})
	}

	return (
		<>
			<Accordion expanded={raFillingAccordionValidation["ancillariesInfo"]} onChange={(e, expanded) => handleAccordionOnChange(e, expanded)}>
				<AccordionSummary expandIcon={<ExpandMore />}>
					<Typography variant={"h6"}>
						{t('raFilling.ancillariesInfo.accordion.title')}
					</Typography>
				</AccordionSummary>

				<AccordionDetails>
					{
						!!counterSaleManagement?.last?.bookedAncillaryList?.length &&
						<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
							<Grid item xs={12}>
								<Typography variant={"bodyLarge"}>
									{t("raFilling.ancillariesInfo.section.bookedAncillaries")}
								</Typography>
							</Grid>

							{
								// LPP-22293 (RDT §3 SCFE-2): accorpamento Change Group (^CG\d+$) coerente con
								// create-ra anche in editing; le altre ancillary restano box separati invariati.
								(() => {
									const {changeGroup, others} = partitionChangeGroup<IRADAncillary>(ancillariesFormValues?.booked ?? []);
									return (
										<>
											{others.map(({ancillary, index}: IIndexedAncillary<IRADAncillary>) =>
												<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'booked'} index={index} ancillaryName={ancillaryName} />
											)}
											{changeGroup.length > 0 &&
												<ChangeGroupField changeGroup={changeGroup} type={'booked'} ancillaryName={ancillaryName} />
											}
										</>
									);
								})()
							}

							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.booked")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.bookedAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.bookedAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
									/>
								</Typography>
							</Grid>
						</Grid>
					}

					{/* Added Ancillaries (remove for keyless-light)*/}
					{
						!!counterSaleManagement?.last?.counterSaleAncillaryList?.length &&
						<Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
							<Grid item xs={12}>
								<Typography variant={"bodyLarge"}>
									{t("raFilling.ancillariesInfo.section.addedAncillaries")}
								</Typography>
							</Grid>

							{
								(() => {
									const {changeGroup, others} = partitionChangeGroup<IRADAncillary>(counterSaleManagement?.last?.counterSaleAncillaryList ?? []);
									return (
										<>
											{others.map(({ancillary, index}: IIndexedAncillary<IRADAncillary>) =>
												<AncillaryField key={`${ancillary.ancillaryCode}`} elem={ancillary} type={'counterSale'} index={index} ancillaryName={ancillaryName} />
											)}
											{changeGroup.length > 0 &&
												<ChangeGroupField changeGroup={changeGroup} type={'counterSale'} ancillaryName={ancillaryName} />
											}
										</>
									);
								})()
							}

							<Grid item xs={12} sx={{pt: theme.spacing(2), display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"body"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.added")}
								</Typography>
								<Typography variant={"body"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.counterSaleAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.counterSaleAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
									/>
								</Typography>
							</Grid>
						</Grid>
					}

					{/* Total Amount */}
					{
						!!counterSaleManagement?.last &&
						<Grid container columnSpacing={2} sx={{pt: theme.spacing(2)}}>
							<Grid item xs={12} sx={{display: "flex", justifyContent: "space-between"}}>
								<Typography variant={"bodyLarge"} component={"span"}>
									{t("raFilling.ancillariesInfo.amount.total")}
								</Typography>

								<Typography variant={"bodyLarge"} component={"span"} sx={{mr: theme.spacing(2)}}>
									<AncillaryTotalCostInfo
										amount={counterSaleManagement?.last?.totalAncillaryAmount?.amountVatIncluded}
										promoAmount={counterSaleManagement?.last?.totalAncillaryAmount?.promoAmountVatIncluded}
										currency={counterSaleManagement?.last?.currency}
										fontWeight={"bodyLarge"}
									/>
								</Typography>
							</Grid>
						</Grid>
					}
				</AccordionDetails>
			</Accordion>
		</>
	);
}

export default AncillariesInfo;

/**
 * LPP-22293 / US-NTWRA-005 (RDT §3 SCFE-2) — contenitore editabile del controllo multi-scelta
 * Change Group in ra-edit. Mirror di create-ra adattato ai campi `IRADAncillary`: la selezione
 * valorizza il form (`ancillaryUnits` 1/0) e `counterSaleManagement` (units 1/0) sulle stesse
 * entità, senza alterare payload, prezzo o totale (Requirement 2, RF-07). Riusa partizione e
 * componente di create-ra (unico punto di verità).
 */
const ChangeGroupField = ({changeGroup, type, ancillaryName}: {changeGroup: IIndexedAncillary<IRADAncillary>[], type: 'booked' | 'counterSale', ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn'}) => {
	const theme = useTheme();
	const raFillingDataForm = useFormContext<IRaDetailView>();
	const [counterSaleManagement, setCounterSaleManagement] = useRecoilState(counterSaleManagementValueState);

	const options: IChangeGroupOption<IRADAncillary>[] = useMemo(
		() => changeGroup.map(({ancillary}) => ({
			code: ancillary.ancillaryCode,
			label: (ancillary[ancillaryName] as string) ?? ancillary.ancillaryCode,
			ancillary,
		})),
		[changeGroup, ancillaryName]
	);

	const groupCodes = new Set(changeGroup.map(({ancillary}) => ancillary.ancillaryCode));

	// Selezione corrente derivata dalla lista reattiva in counterSaleManagement (fonte del payload).
	const list = type === 'booked'
		? counterSaleManagement?.last?.bookedAncillaryList
		: counterSaleManagement?.last?.counterSaleAncillaryList;
	const selectedCodes = (list ?? [])
		.filter((a: IRADAncillary) => groupCodes.has(a.ancillaryCode))
		.filter((a: IRADAncillary) => (Number(a.units ?? 0) > 0) || (a.units as unknown) === true)
		.map((a: IRADAncillary) => a.ancillaryCode);

	const disabled = changeGroup.some(({ancillary}) => !ancillary.deselectable);

	const handleChange = (nextSelected: string[]) => {
		const selected = new Set(nextSelected);

		// 1) aggiorna il form (ancillaryUnits 1/0) sugli indici originali, coerente con AncillaryField
		changeGroup.forEach(({ancillary, index}) => {
			raFillingDataForm.setValue(
				`ancillariesInfo.${type}.${index}.ancillaryUnits` as any,
				selected.has(ancillary.ancillaryCode) ? 1 : 0
			);
		});

		// 2) aggiorna counterSaleManagement (payload counter-sale) con le stesse entità
		const csmClone = deepClone(counterSaleManagement.last);
		const targetList = type === 'booked' ? csmClone.bookedAncillaryList : csmClone.counterSaleAncillaryList;
		(targetList ?? []).forEach((ancillary: IRADAncillary) => {
			if (groupCodes.has(ancillary.ancillaryCode)) {
				ancillary.units = selected.has(ancillary.ancillaryCode) ? 1 : 0;
			}
		});
		setCounterSaleManagement({previous: counterSaleManagement.last, last: csmClone});
	};

	return (
		<Grid container item xs={12} sx={{py: theme.spacing(1)}}>
			<Grid item xs={12}>
				<ChangeGroupMultiSelect<IRADAncillary>
					testId={type}
					options={options}
					selectedCodes={selectedCodes}
					onChange={handleChange}
					disabled={disabled}
					renderPrice={(ancillary: IRADAncillary) => <AncillaryCostInfo elem={ancillary} />}
				/>
			</Grid>
		</Grid>
	);
};

/**
 * Ancillary cost info - cost currency /unit
 */
const AncillaryCostInfo = ({elem}: {elem: IRADAncillary}) => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				(elem.ancillaryExtensionCost/*promoPriceVatIncluded*/ === null  || elem.ancillaryExtensionCost/*promoPriceVatIncluded*/ === undefined) ?
					<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(0.5)}}>
						{formatCurrency(locale as LOCALES, elem.priceVatIncluded /*priceVatIncluded*/, elem.ancillaryCurrency as string)}{elem.mileageNeeded ? '/km' : (elem.maxUnits > 1 || elem.ancillaryUnits > 1) ? '/u' : ''}
					</Typography> :
					<>
						<Typography variant={'bodySmall'} component={'span'}>
							(
							<span style={{textDecoration: 'line-through'}}>
								{formatCurrency(locale as LOCALES,elem.priceVatIncluded /*priceVatIncluded*/, elem.ancillaryCurrency as string)}
							</span>
							)
						</Typography>
						<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(1)}}>
							{formatCurrency(locale as LOCALES, elem.priceVatIncluded /*promoPriceVatIncluded*/, elem.ancillaryCurrency as string)}{elem.mileageNeeded ? '/km' : (elem.maxUnits > 1 || elem.ancillaryUnits > 1) ? '/u' : ''}
						</Typography>
					</>
			}
		</>
	);
}

/**
 * Ancillary cost info - cost currency /unit
 */
export const AncillaryTotalCostInfo = ({amount, promoAmount, currency, fontWeight = 'body'}: {amount: number | null; promoAmount: number | null, currency: string, fontWeight?: 'body' | 'bodyLarge'}) => {
	const theme = useTheme();
	const {i18n} = useTranslation(['network']);

	

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
		return getLocaleByLang(i18n.language);
	}, [i18n.language]);

	return (
		<>
			{
				(promoAmount === null || promoAmount === undefined) ?
					<Typography variant={'body'} component={'span'} sx={{mx: theme.spacing(0.5)}}>
						<strong>{formatCurrency(locale as LOCALES, amount, currency)}</strong>
					</Typography> :
					<>
						<Typography variant={'bodySmall'} component={'span'}>
							<strong>(
								<span style={{textDecoration: 'line-through'}}>
									{formatCurrency(locale as LOCALES, amount, currency)}
								</span>
							)</strong>
						</Typography>
						<Typography variant={fontWeight} component={'span'} sx={{mx: theme.spacing(1)}}>
							<strong>{formatCurrency(locale as LOCALES, promoAmount, currency)}</strong>
						</Typography>
					</>
			}
		</>
	);
}

const AncillaryField = ({elem, type, index, ancillaryName}: {elem: IRADAncillary, type: 'booked' | 'counterSale', index: number, ancillaryName: 'ancillaryNameIt' | 'ancillaryNameEn'}) => {

	const theme = useTheme();
	const raFillingDataForm = useFormContext<IRaDetailView>();
	const [counterSaleManagement, setCounterSaleManagement] = useRecoilState(counterSaleManagementValueState);

	const fieldName = useMemo(() => {
		return `ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'ancillaryMileage' : 'ancillaryUnits'}` // Changed;
	}, [type, index, elem.mileageNeeded]);

	const fieldValue = useWatch<IRaDetailView>({
		name: fieldName as any,
	}) as number | undefined;

	useEffect(() => {
		raFillingDataForm.setValue(fieldName as any, elem.mileageNeeded ? elem.mileage : elem.units);
		raFillingDataForm.setValue(`ancillariesInfo.${type}.${index}.ancillaryCode`, elem.ancillaryCode);
		console.log(elem.mileageNeeded ? { elem: elem.mileage } : { elem: elem.units });
	}, [elem]);

	useEffect(() => {
		console.log(fieldValue, "csm", elem.ancillaryNameEn);
	}, [fieldValue])

	const manageAncillary = async () => {
		const csmClone = deepClone(counterSaleManagement.last);
		const list = type === 'booked' ? 'bookedAncillaryList': 'counterSaleAncillaryList';
	

		if (_.isBoolean(fieldValue)) {
			csmClone[list]?.every((ancillary: IRADAncillary) => {
				if (ancillary.ancillaryCode === elem.ancillaryCode) {
					ancillary.units = fieldValue ? 1 : 0;		//CHANGED
					return false;
				}
				return true;
			});
		} else {
			csmClone[list]?.every((ancillary: IRADAncillary) => {
				if (ancillary.ancillaryCode === elem.ancillaryCode) {
					if (ancillary.mileageNeeded) {
						ancillary.mileage = fieldValue as number; //CHANGED
					} else {
						ancillary.units = fieldValue as number; //CHANGED
					}
					return false;
				}
				return true;
			});
		}
		setCounterSaleManagement({previous: counterSaleManagement.last, last: csmClone});
	}

	useEffect(() => {
		manageAncillary();
	}, [fieldValue])

	const storeTextValue = (event: React.SyntheticEvent, ancillary: IRADAncillary, type: 'booked' | 'counterSale') => {
		const cs = deepClone(counterSaleManagement);
		let list = [];
		if (type === 'booked') {
			list = cs.last.bookedAncillaryList;
		} else {
			list = cs.last.counterSaleAncillaryList;
		}

		for (let i = 0; i < list.length; i++) {
			if (list[i].ancillaryCode === ancillary.ancillaryCode) {
				// @ts-ignore
				list[i].address = event?.target?.value;
				break;
			}
		}

		setCounterSaleManagement({previous: counterSaleManagement.last, last: cs.last});
	}

	// Mostra stepper numerico se: maxUnits > 1, mileageNeeded, oppure ancillaryUnits > 1
	// (quest'ultimo caso copre gli accessori post-firma dove maxUnits non arriva dal catalogo PRC).
	if (elem.maxUnits > 1 || elem.mileageNeeded || elem.ancillaryUnits > 1) {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1), mb: 'auto'}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start"}}>
					<Box sx={{width: '100px'}}>
						<FormNumberInput<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.${elem.mileageNeeded ? 'ancillaryMileage' : 'ancillaryUnits'}`}
							label={''}
							control={raFillingDataForm.control}
							minValue={0}
							maxValue={elem.ancillaryMileage ? 999 : (elem.maxUnits > 1 ? elem.maxUnits : elem.ancillaryUnits)}
							//CHANGED
							disabled={!elem.deselectable || elem.ancillarySubType === AncillarySubtype.AdditionalDrivers || elem.ancillarySubType === AncillarySubtype.Keyless}
	
						/>
						<FormTextField<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.ancillaryCode`}
							label={''}
							control={raFillingDataForm.control}
							style={{ display: 'none' }}
						/>	
					</Box>
					<Tooltip title={elem[ancillaryName] as string} disableInteractive sx={{pt: theme.spacing(2)}}>
						<Typography variant={'body'} sx={{ml: theme.spacing(2), py: theme.spacing(1)}}>
							{elem[ancillaryName]}
						</Typography>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					(['DELIVERY', 'COLLECTION'].includes(elem.ancillarySubType as string) || ['DELIVERY', 'COLLECTION'].includes(elem.ancillarySubType as string)) && !!fieldValue && fieldValue > 0 &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.address`}
							label={elem[ancillaryName]}
							control={raFillingDataForm.control}
							required={!!fieldValue && fieldValue > 0}
							//disabled={bookingDataValue.bookingDetail.flagSubstitution}
							onBlur={(event: React.SyntheticEvent) => storeTextValue(event, elem, type)}
						/>
					</Grid>
				}
			</Grid>
		)
	} else {
		return (
			<Grid container item xs={12} key={`${type}_${elem.ancillaryCode}`} sx={{py: theme.spacing(1)}}>
				<Grid item xs={8} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pt: theme.spacing(2)}}>
					<Tooltip title={elem.ancillaryDescr ?? ''} disableInteractive sx={{display: "flex", alignItems: "flex-end"}}>
						<span>
							<FormCheckbox<IRaDetailView>
								name={`ancillariesInfo.${type}.${index}.ancillaryUnits`}
								label={elem[ancillaryName]}
								control={raFillingDataForm.control}
								disabled={!elem.deselectable}
                                readOnly={!elem.deselectable || 
									 elem.ancillarySubType === AncillarySubtype.AdditionalDrivers
									/*|| elem.subtype === AncillarySubtype.Keyless*/ || elem.ancillarySubType === AncillarySubtype.Keyless
								}
								//{...`ancillariesInfo.${type}.${index}.units`}
								// defaultValue={['A', 'Z'].includes(params.sellType) ? true : false}
								// disabled={['A', 'Z'].includes(params.sellType) || step === InsertBookingStep.CUSTOMER_DETAILS}
							/>
						</span>
					</Tooltip>
				</Grid>
				<Grid item xs={4} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', py: theme.spacing(1), pr: theme.spacing(2)}}>
					<AncillaryCostInfo elem={elem}/>
				</Grid>

				{
					(['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.ancillarySubType as string) || ['URBAN_DELIVERY', 'URBAN_COLLECTION'].includes(elem.ancillarySubType as string)) && !!fieldValue &&
					<Grid item xs={12} sx={{display: "flex", alignItems: "flex-end", justifyContent: "flex-start", pr: theme.spacing(2)}}>
						<FormTextField<IRaDetailView>
							name={`ancillariesInfo.${type}.${index}.address`}
							label={elem[ancillaryName]}
							control={raFillingDataForm.control}
							required={!!fieldValue}
							//disabled={bookingDataValue.bookingDetail.flagSubstitution}
							onBlur={(event: React.SyntheticEvent) => storeTextValue(event, elem, type)}
						/>
					</Grid>
				}
			</Grid>
		)
	}
}
